import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2, FileText, Search, Building2, MapPin, ChevronRight,
  Download, Save, ArrowLeft, Gavel, Scale, ClipboardList,
  FolderOpen, CheckCircle2, Filter, Sparkles
} from "lucide-react";
import { API } from "@/constants";

const CATEGORY_ICONS = { initiating: Gavel, responsive: FileText, motions: Scale, discovery: Search, management: ClipboardList, trial: Gavel, settlement: CheckCircle2, general: FolderOpen };
const CATEGORY_COLORS = { initiating: "bg-blue-100 text-blue-700", responsive: "bg-purple-100 text-purple-700", motions: "bg-red-100 text-red-700", discovery: "bg-amber-100 text-amber-700", management: "bg-slate-100 text-slate-700", trial: "bg-indigo-100 text-indigo-700", settlement: "bg-emerald-100 text-emerald-700", general: "bg-cyan-100 text-cyan-700" };

// ─── Step 1: District Browser ──────────────────────────────
const DistrictBrowser = ({ onSelectDistrict, onSelectForm }) => {
  const [districts, setDistricts] = useState([]);
  const [filter, setFilter] = useState("all"); // all | federal | state
  const [stateFilter, setStateFilter] = useState("");
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetch(`${API}/court-forms/districts`)
      .then(r => r.json()).then(j => setDistricts(j.data || [])).catch(() => {});
  }, []);

  const handleSearch = async () => {
    if (!search.trim()) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const r = await fetch(`${API}/court-forms/forms?search=${encodeURIComponent(search)}`);
      const j = await r.json();
      setSearchResults(j.data || []);
    } catch { setSearchResults([]); } finally { setSearching(false); }
  };

  const filtered = districts.filter(d => {
    if (filter !== "all" && d.type !== filter) return false;
    if (stateFilter && (d.state || "").toUpperCase() !== stateFilter.toUpperCase()) return false;
    return true;
  });

  const states = [...new Set(districts.map(d => d.state).filter(Boolean))].sort();
  const federalCircuits = filtered.filter(d => d.parent === "circuit");
  const federalDistricts = filtered.filter(d => d.parent === "district");
  const stateCourts = filtered.filter(d => d.type === "state");

  return (
    <div className="space-y-5">
      {/* Search Bar */}
      <Card className="border-0 shadow-sm" style={{ background: "linear-gradient(135deg, #EFF6FF, #DBEAFE)" }}>
        <CardContent className="pt-5">
          <div className="flex items-center gap-2 mb-3">
            <Search className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-bold text-blue-800">Search All Court Forms</span>
          </div>
          <div className="flex gap-2">
            <Input
              value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSearch()}
              placeholder="Search by form name, number, or keyword..."
              className="flex-1 bg-white border-blue-200" data-testid="forms-search"
            />
            <Button onClick={handleSearch} className="bg-blue-600 hover:bg-blue-700 shrink-0" disabled={searching} data-testid="forms-search-btn">
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-1" />Search</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Search Results */}
      {searchResults && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-600" />
              {searchResults.length} Forms Found
              <button onClick={() => setSearchResults(null)} className="ml-auto text-xs text-slate-500 hover:text-slate-700">Clear</button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {searchResults.map(form => (
                <button key={form.id} onClick={() => onSelectForm(form.id)} className="w-full text-left p-3 rounded-xl bg-slate-50 hover:bg-blue-50 transition-colors">
                  <div className="flex items-center gap-2">
                    <Badge className={CATEGORY_COLORS[form.category] || "bg-slate-100 text-slate-700"}>{form.number}</Badge>
                    <span className="text-sm font-medium text-slate-800">{form.name}</span>
                    <ChevronRight className="h-3.5 w-3.5 ml-auto text-slate-400" />
                  </div>
                  <p className="text-xs text-slate-500 mt-1 ml-1">{form.description}</p>
                </button>
              ))}
              {searchResults.length === 0 && <p className="text-sm text-slate-500 text-center py-4">No forms match your search.</p>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <Filter className="h-4 w-4 text-slate-500" />
        {["all", "federal", "state"].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${filter === f ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid={`filter-${f}`}>
            {f === "all" ? "All Courts" : f === "federal" ? "Federal" : "State"}
          </button>
        ))}
        {states.length > 0 && (
          <Select value={stateFilter} onValueChange={v => setStateFilter(v === "all-states" ? "" : v)}>
            <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue placeholder="Filter state..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all-states">All States</SelectItem>
              {states.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Federal Circuits */}
      {(filter === "all" || filter === "federal") && federalCircuits.length > 0 && (
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Federal Circuit Courts</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {federalCircuits.map(d => (
              <button key={d.id} onClick={() => onSelectDistrict(d)} className="p-3 rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50 text-left transition-all" data-testid={`district-${d.id}`}>
                <p className="text-xs font-semibold text-slate-700">{d.name}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">Federal</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Federal Districts */}
      {(filter === "all" || filter === "federal") && federalDistricts.length > 0 && (
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Federal District Courts</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {federalDistricts.map(d => (
              <button key={d.id} onClick={() => onSelectDistrict(d)} className="p-3 rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50 text-left transition-all" data-testid={`district-${d.id}`}>
                <p className="text-xs font-semibold text-slate-700">{d.name}</p>
                <div className="flex items-center gap-1 mt-0.5">
                  <MapPin className="h-3 w-3 text-slate-400" />
                  <p className="text-[10px] text-slate-400">{d.state}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* State Courts */}
      {(filter === "all" || filter === "state") && stateCourts.length > 0 && (
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">State Courts</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {stateCourts.map(d => (
              <button key={d.id} onClick={() => onSelectDistrict(d)} className="p-3 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 text-left transition-all" data-testid={`district-${d.id}`}>
                <p className="text-xs font-semibold text-slate-700">{d.name}</p>
                <div className="flex items-center gap-1 mt-0.5">
                  <Building2 className="h-3 w-3 text-slate-400" />
                  <p className="text-[10px] text-slate-400">{d.state} State</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Step 2: Form List for District ────────────────────────
const FormList = ({ district, onSelectForm, onBack }) => {
  const [forms, setForms] = useState([]);
  const [categories, setCategories] = useState({});
  const [catFilter, setCatFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/court-forms/forms?district=${district.id}`)
      .then(r => r.json()).then(j => { setForms(j.data || []); setCategories(j.categories || {}); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [district.id]);

  const filtered = catFilter === "all" ? forms : forms.filter(f => f.category === catFilter);
  const grouped = filtered.reduce((acc, f) => { (acc[f.category] = acc[f.category] || []).push(f); return acc; }, {});

  return (
    <div className="space-y-5">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700" data-testid="back-to-districts">
        <ArrowLeft className="h-4 w-4" />Back to Courts
      </button>
      <div className="rounded-2xl p-5 text-white" style={{ background: district.type === "federal" ? "linear-gradient(135deg, #1E3A5F, #2563EB)" : "linear-gradient(135deg, #065F46, #059669)" }}>
        <h2 className="text-lg font-bold">{district.name}</h2>
        <p className="text-sm opacity-80">{district.type === "federal" ? "Federal Court" : "State Court"} {district.state ? `- ${district.state}` : ""}</p>
        <p className="text-xs opacity-60 mt-1">{forms.length} forms available</p>
      </div>
      {/* Category Filters */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setCatFilter("all")} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${catFilter === "all" ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600"}`}>All ({forms.length})</button>
        {Object.entries(categories).filter(([k]) => forms.some(f => f.category === k)).map(([k, v]) => (
          <button key={k} onClick={() => setCatFilter(k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${catFilter === k ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600"}`}>{v}</button>
        ))}
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).map(([cat, catForms]) => {
            const Icon = CATEGORY_ICONS[cat] || FileText;
            return (
              <div key={cat}>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" />{categories[cat] || cat}</h3>
                <div className="space-y-2">
                  {catForms.map(form => (
                    <button key={form.id} onClick={() => onSelectForm(form.id)} className="w-full text-left p-4 rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all" data-testid={`form-${form.id}`}>
                      <div className="flex items-center gap-2">
                        <Badge className={CATEGORY_COLORS[form.category] || "bg-slate-100"}>{form.number}</Badge>
                        <span className="text-sm font-semibold text-slate-800">{form.name}</span>
                        <ChevronRight className="h-4 w-4 ml-auto text-slate-400" />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{form.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && <p className="text-sm text-slate-500 text-center py-4">No forms in this category.</p>}
        </div>
      )}
    </div>
  );
};

// ─── Step 3: Editable Form Editor ──────────────────────────
const FormEditor = ({ formId, onBack }) => {
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [aiDesc, setAiDesc] = useState("");
  const [aiFilling, setAiFilling] = useState(false);
  const [aiConfidence, setAiConfidence] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/court-forms/form/${formId}`)
      .then(r => r.json()).then(j => { setForm(j.data); setValues({}); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [formId]);

  const handleAIFill = async () => {
    if (!aiDesc.trim()) return;
    setAiFilling(true);
    try {
      const r = await fetch(`${API}/court-forms/ai-fill`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ form_id: formId, case_description: aiDesc, existing_values: values })
      });
      const j = await r.json();
      if (j.data?.field_values) {
        setValues(prev => ({ ...prev, ...Object.fromEntries(Object.entries(j.data.field_values).filter(([, v]) => v)) }));
        setAiConfidence(j.data.confidence);
      }
    } catch (e) { console.error(e); } finally { setAiFilling(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/court-forms/save`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ form_id: formId, field_values: values })
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { console.error(e); } finally { setSaving(false); }
  };

  const handlePrint = () => {
    const w = window.open("", "_blank");
    if (!w || !form) return;
    const fields = form.fields.map(f => `
      <div style="margin-bottom:12px;">
        <label style="font-weight:600;font-size:11px;text-transform:uppercase;color:#666;">${f.label}</label>
        <div style="border-bottom:1px solid #ccc;padding:4px 2px;min-height:24px;font-size:14px;">${values[f.name] || ""}</div>
      </div>
    `).join("");
    w.document.write(`<!DOCTYPE html><html><head><title>${form.number} - ${form.name}</title>
      <style>body{font-family:Georgia,serif;max-width:700px;margin:40px auto;padding:0 20px;color:#333;}
      h1{font-size:18px;text-align:center;border-bottom:2px solid #000;padding-bottom:10px;margin-bottom:20px;}
      h2{font-size:14px;text-align:center;color:#666;margin-bottom:30px;}
      @media print{body{margin:0;padding:20px;}}</style></head>
      <body><h1>${form.number} - ${form.name}</h1><h2>Court Form</h2>${fields}
      <div style="margin-top:40px;text-align:right;font-size:12px;color:#999;">Generated by Trialeze Court Forms Hub</div>
      <script>setTimeout(()=>window.print(),500)</script></body></html>`);
    w.document.close();
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!form) return <p className="text-center text-slate-500">Form not found.</p>;

  return (
    <div className="space-y-5">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700" data-testid="back-from-editor">
        <ArrowLeft className="h-4 w-4" />Back
      </button>
      {/* Form Header */}
      <div className="rounded-2xl p-5" style={{ background: "linear-gradient(135deg, #1E293B, #334155)" }}>
        <div className="flex items-center gap-2 text-white">
          <Badge className="bg-white/20 text-white">{form.number}</Badge>
          <h2 className="text-lg font-bold">{form.name}</h2>
        </div>
        <p className="text-sm text-slate-300 mt-1">{form.description}</p>
      </div>
      {/* AI Fill Section */}
      <Card className="border-0 shadow-sm" style={{ background: "linear-gradient(135deg, #EFF6FF, #DBEAFE)" }}>
        <CardContent className="pt-5">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-bold text-blue-800">AI Form Fill</span>
            {aiConfidence !== null && <Badge className="bg-blue-100 text-blue-700 ml-auto">{aiConfidence}% filled</Badge>}
          </div>
          <p className="text-xs text-blue-600 mb-2">Describe your case in plain English and AI will auto-populate all fields</p>
          <div className="flex gap-2">
            <Input value={aiDesc} onChange={e => setAiDesc(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleAIFill()}
              placeholder="e.g., Personal injury case, John Smith vs Acme Corp, auto accident on I-405 on Jan 15 2025, filing in CDCA..."
              className="flex-1 bg-white border-blue-200 text-sm" data-testid="ai-fill-input" />
            <Button onClick={handleAIFill} disabled={aiFilling || !aiDesc.trim()} className="bg-blue-600 hover:bg-blue-700 shrink-0" data-testid="ai-fill-btn">
              {aiFilling ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1" />Fill</>}
            </Button>
          </div>
        </CardContent>
      </Card>
      {/* Editable Fields */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Fill in the fields below. All fields are editable.</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {form.fields.map(field => {
              const isWide = field.width === "full" || field.type === "textarea";
              const cls = isWide ? "md:col-span-2" : "";
              return (
                <div key={field.name} className={cls}>
                  <Label className="text-xs font-semibold text-slate-700">
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </Label>
                  {field.type === "textarea" ? (
                    <Textarea value={values[field.name] || ""} onChange={e => setValues(v => ({ ...v, [field.name]: e.target.value }))}
                      placeholder={`Enter ${field.label.toLowerCase()}...`} className="mt-1 min-h-[80px]"
                      data-testid={`field-${field.name}`} />
                  ) : field.type === "select" ? (
                    <Select value={values[field.name] || ""} onValueChange={v => setValues(vv => ({ ...vv, [field.name]: v }))}>
                      <SelectTrigger className="mt-1" data-testid={`field-${field.name}`}><SelectValue placeholder={`Select ${field.label.toLowerCase()}...`} /></SelectTrigger>
                      <SelectContent>
                        {(field.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input type={field.type || "text"} value={values[field.name] || ""}
                      onChange={e => setValues(v => ({ ...v, [field.name]: e.target.value }))}
                      placeholder={`Enter ${field.label.toLowerCase()}...`} className="mt-1"
                      data-testid={`field-${field.name}`} />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      {/* Actions */}
      <div className="flex gap-3">
        <Button onClick={handleSave} disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 h-11" data-testid="save-form-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : saved ? <CheckCircle2 className="h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          {saved ? "Saved!" : "Save Form"}
        </Button>
        <Button onClick={handlePrint} variant="outline" className="flex-1 h-11" data-testid="download-form-btn">
          <Download className="h-4 w-4 mr-2" />Download / Print
        </Button>
      </div>
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────
const CourtFormsPage = () => {
  const [step, setStep] = useState("browse"); // browse | list | edit
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedFormId, setSelectedFormId] = useState(null);

  const handleSelectDistrict = (d) => { setSelectedDistrict(d); setStep("list"); };
  const handleSelectForm = (fid) => { setSelectedFormId(fid); setStep("edit"); };
  const handleBackToDistricts = () => { setStep("browse"); setSelectedDistrict(null); };
  const handleBackFromEditor = () => {
    if (selectedDistrict) setStep("list");
    else setStep("browse");
  };

  return (
    <div className="max-w-5xl mx-auto py-6 px-4 space-y-6" data-testid="court-forms-hub">
      {/* Header */}
      <div className="rounded-2xl p-6 text-white" style={{ background: "linear-gradient(135deg, #1E3A5F, #1D4ED8)" }}>
        <div className="flex items-center gap-3 mb-1">
          <FileText className="h-7 w-7 text-blue-300" />
          <h1 className="text-2xl font-black tracking-tight">Court Forms Hub</h1>
          <Badge className="bg-blue-500/20 text-blue-200 border-blue-500/30">ALL DISTRICTS</Badge>
        </div>
        <p className="text-blue-200 text-sm">Browse by court district, search forms, auto-fill with case data, edit and download</p>
        {/* Breadcrumb */}
        <div className="flex items-center gap-1 mt-3 text-xs text-blue-300">
          <button onClick={handleBackToDistricts} className={step === "browse" ? "font-bold text-white" : "hover:text-white"}>Courts</button>
          {step !== "browse" && selectedDistrict && (
            <>
              <ChevronRight className="h-3 w-3" />
              <button onClick={() => setStep("list")} className={step === "list" ? "font-bold text-white" : "hover:text-white"}>{selectedDistrict.name}</button>
            </>
          )}
          {step === "edit" && (
            <>
              <ChevronRight className="h-3 w-3" />
              <span className="font-bold text-white">Edit Form</span>
            </>
          )}
        </div>
      </div>

      {step === "browse" && <DistrictBrowser onSelectDistrict={handleSelectDistrict} onSelectForm={handleSelectForm} />}
      {step === "list" && selectedDistrict && <FormList district={selectedDistrict} onSelectForm={handleSelectForm} onBack={handleBackToDistricts} />}
      {step === "edit" && selectedFormId && <FormEditor formId={selectedFormId} onBack={handleBackFromEditor} />}
    </div>
  );
};

export default CourtFormsPage;
